import React, { useState, useEffect } from 'react';
import { Bell, Plus, Check, Clock, Trash2, Calendar, ChevronDown } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { remindersAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function RemindersPage() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminder_type: 'daily',
    time_of_day: '',
    day_of_week: null,
    is_recurring: true,
  });

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    try {
      const response = await remindersAPI.getAll();
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Failed to load reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const data = {
        ...formData,
        time_of_day: formData.time_of_day || null,
        day_of_week: formData.reminder_type === 'weekly' ? parseInt(formData.day_of_week) : null,
      };
      await remindersAPI.create(data);
      toast.success('Reminder added!');
      setShowAddDialog(false);
      setFormData({
        title: '',
        description: '',
        reminder_type: 'daily',
        time_of_day: '',
        day_of_week: null,
        is_recurring: true,
      });
      loadReminders();
    } catch (error) {
      toast.error('Failed to add reminder');
    }
  };

  const handleComplete = async (reminderId) => {
    try {
      await remindersAPI.complete(reminderId);
      setReminders(prev => prev.map(r => {
        if (r.id === reminderId) {
          const today = new Date().toISOString().split('T')[0];
          return {
            ...r,
            completions: [...(r.completions || []), today]
          };
        }
        return r;
      }));
      toast.success('Reminder completed!');
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    try {
      await remindersAPI.delete(reminderId);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      toast.success('Reminder deleted');
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  
  const isCompletedToday = (reminder) => {
    return (reminder.completions || []).includes(today);
  };

  const groupedReminders = {
    daily: reminders.filter(r => r.reminder_type === 'daily'),
    weekly: reminders.filter(r => r.reminder_type === 'weekly'),
    one_time: reminders.filter(r => r.reminder_type === 'one_time'),
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="reminders-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Reminders</h1>
            <p className="text-[#4A2C2A]/60 mt-1">Manage your daily wellness tasks</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl" data-testid="add-reminder-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Reminder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New Reminder</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddReminder} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Take morning supplements"
                    required
                    data-testid="reminder-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Add any notes..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={formData.reminder_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, reminder_type: value }))}
                    >
                      <SelectTrigger data-testid="reminder-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="one_time">One-time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Time (optional)</Label>
                    <Input
                      type="time"
                      value={formData.time_of_day}
                      onChange={(e) => setFormData(prev => ({ ...prev, time_of_day: e.target.value }))}
                      data-testid="reminder-time-input"
                    />
                  </div>
                </div>
                {formData.reminder_type === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={formData.day_of_week?.toString() || '0'}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, day_of_week: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dayNames.map((day, i) => (
                          <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="recurring"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked }))}
                    className="rounded border-[#E8A095]/30"
                    disabled={formData.reminder_type === 'one_time'}
                  />
                  <Label htmlFor="recurring" className="cursor-pointer text-sm">
                    Recurring (repeats automatically)
                  </Label>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120]"
                  data-testid="save-reminder-btn"
                >
                  Add Reminder
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Daily Reminders */}
        {groupedReminders.daily.length > 0 && (
          <div className="soft-card p-6">
            <h2 className="font-heading text-lg text-[#4A2C2A] mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#E8A095]" /> Daily Tasks
            </h2>
            <div className="space-y-3">
              {groupedReminders.daily.map(reminder => (
                <ReminderItem 
                  key={reminder.id}
                  reminder={reminder}
                  isCompleted={isCompletedToday(reminder)}
                  onComplete={() => handleComplete(reminder.id)}
                  onDelete={() => handleDelete(reminder.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Weekly Reminders */}
        {groupedReminders.weekly.length > 0 && (
          <div className="soft-card p-6">
            <h2 className="font-heading text-lg text-[#4A2C2A] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#8DA399]" /> Weekly Tasks
            </h2>
            <div className="space-y-3">
              {groupedReminders.weekly.map(reminder => (
                <ReminderItem 
                  key={reminder.id}
                  reminder={reminder}
                  isCompleted={isCompletedToday(reminder)}
                  onComplete={() => handleComplete(reminder.id)}
                  onDelete={() => handleDelete(reminder.id)}
                  showDay
                />
              ))}
            </div>
          </div>
        )}

        {/* One-time Reminders */}
        {groupedReminders.one_time.length > 0 && (
          <div className="soft-card p-6">
            <h2 className="font-heading text-lg text-[#4A2C2A] mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#E8C495]" /> One-time Tasks
            </h2>
            <div className="space-y-3">
              {groupedReminders.one_time.map(reminder => (
                <ReminderItem 
                  key={reminder.id}
                  reminder={reminder}
                  isCompleted={isCompletedToday(reminder)}
                  onComplete={() => handleComplete(reminder.id)}
                  onDelete={() => handleDelete(reminder.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {reminders.length === 0 && (
          <div className="soft-card p-12 text-center">
            <Bell className="w-12 h-12 mx-auto mb-4 text-[#4A2C2A]/30" />
            <h2 className="font-heading text-xl text-[#4A2C2A] mb-2">No Reminders Yet</h2>
            <p className="text-[#4A2C2A]/60 mb-6">
              Add reminders to keep track of your daily wellness tasks
            </p>
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Your First Reminder
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function ReminderItem({ reminder, isCompleted, onComplete, onDelete, showDay = false }) {
  return (
    <div 
      className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
        isCompleted ? 'bg-[#8DA399]/10' : 'bg-[#FDFBF7]'
      }`}
    >
      <button
        onClick={onComplete}
        disabled={isCompleted}
        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
          isCompleted 
            ? 'border-[#8DA399] bg-[#8DA399]' 
            : 'border-[#E8A095]/30 hover:border-[#E8A095] hover:bg-[#E8A095]/10'
        }`}
        data-testid={`complete-${reminder.id}`}
      >
        {isCompleted && <Check className="w-4 h-4 text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`font-medium ${isCompleted ? 'text-[#4A2C2A]/50 line-through' : 'text-[#4A2C2A]'}`}>
          {reminder.title}
        </p>
        <div className="flex items-center gap-3 text-xs text-[#4A2C2A]/50">
          {reminder.time_of_day && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {reminder.time_of_day}
            </span>
          )}
          {showDay && reminder.day_of_week !== null && (
            <span>{dayNames[reminder.day_of_week]}</span>
          )}
          {reminder.created_by === 'ai' && (
            <span className="px-2 py-0.5 bg-[#95B8E8]/20 text-[#95B8E8] rounded">AI Suggested</span>
          )}
          {reminder.created_by === 'admin' && (
            <span className="px-2 py-0.5 bg-[#E8A095]/20 text-[#E8A095] rounded">From Provider</span>
          )}
        </div>
        {reminder.description && (
          <p className="text-sm text-[#4A2C2A]/60 mt-1">{reminder.description}</p>
        )}
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-[#4A2C2A]/40 hover:text-red-500">
            <Trash2 className="w-4 h-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reminder?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this reminder.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
